import React, { useMemo } from 'react';
import { Card, Typography, Box, Stack, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { merge } from 'lodash';
import BaseOptionChart, { BaseChart } from 'src/components/charts/BaseOptionChart';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
}));

const MetricCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter} 0%, ${theme.palette.primary.light}20 100%)`,
  border: `1px solid ${theme.palette.primary.light}40`,
  textAlign: 'center',
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

// ----------------------------------------------------------------------

const DashboardStatsChart = ({ 
  title, 
  data, 
  isLoading = false, 
  error = null,
  chartType = 'line',
  height = 300,
  showMetrics = true 
}) => {
  const chartOptions = useMemo(() => {
    const baseOptions = merge(BaseOptionChart(), {
      chart: {
        type: chartType,
        height: { xs: 250, sm: 300, md: height },
        toolbar: { show: false },
        animations: { enabled: false }
      },
      stroke: {
        width: chartType === 'area' ? [3, 2, 1] : [2, 2, 2],
        curve: 'smooth'
      },
      fill: {
        type: chartType === 'area' ? ['gradient', 'gradient', 'gradient'] : ['solid', 'solid', 'solid'],
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: ['#fff', '#fff', '#fff'],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.1,
          stops: [0, 100]
        }
      },
      colors: ['#3182CE', '#38A169', '#E53E3E', '#805AD5', '#D69E2E'],
      xaxis: {
        type: 'datetime',
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          },
          formatter: (value) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
            return value.toString();
          }
        }
      },
      tooltip: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        y: {
          formatter: (value) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
            if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
            return value.toLocaleString();
          }
        }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '13px',
        fontWeight: 500,
        markers: {
          radius: 6,
        },
        itemMargin: {
          horizontal: 12,
        },
        labels: {
          colors: '#4A5568'
        }
      },
      grid: {
        show: true,
        borderColor: '#E2E8F0',
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      }
    });

    return baseOptions;
  }, [chartType, height]);

  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height + 100 }}>
          <CircularProgress />
        </Box>
      </StyledCard>
    );
  }

  if (error) {
    return (
      <StyledCard>
        <Alert severity="error">
          Failed to load chart data. Please try again.
        </Alert>
      </StyledCard>
    );
  }

  if (!data || !data.series || data.series.length === 0) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height + 100 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No data available
          </Typography>
        </Box>
      </StyledCard>
    );
  }

  const totalValue = data.series.reduce((sum, series) => {
    return sum + series.data.reduce((seriesSum, value) => seriesSum + value, 0);
  }, 0);

  const currentValue = data.series[0]?.data[data.series[0]?.data.length - 1] || 0;
  const previousValue = data.series[0]?.data[data.series[0]?.data.length - 2] || 0;
  const growthRate = previousValue > 0 ? ((currentValue - previousValue) / previousValue * 100).toFixed(1) : 0;

  return (
    <StyledCard>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        {showMetrics && (
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <MetricCard sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {totalValue >= 1000000 ? `${(totalValue / 1000000).toFixed(1)}M` : 
                 totalValue >= 1000 ? `${(totalValue / 1000).toFixed(1)}K` : 
                 totalValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total
              </Typography>
            </MetricCard>
            <MetricCard sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: growthRate >= 0 ? 'success.main' : 'error.main' }}>
                {growthRate >= 0 ? '+' : ''}{growthRate}%
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Growth
              </Typography>
            </MetricCard>
          </Stack>
        )}
      </Box>

      <Box sx={{ flex: 1 }}>
        <BaseChart
          series={data.series}
          options={chartOptions}
          type={chartType}
          height={height}
        />
      </Box>
    </StyledCard>
  );
};

export default DashboardStatsChart;
